import { Box, Button, CloseButton, Flex } from '@amzn/ring-ui-react-components';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { checkSubscriptionWithToggler } from 'src/components/Posting/Steps/RingHistory';
import {
  Header,
  VideoPlayer,
  VideoPlayerHeader,
} from 'src/components/Posting/components/RingHistory';
import {
  defaultDevice,
  useGetDevicesByLocationId,
  useGetRingLocations,
  useGetVideosByDeviceId,
  useGetVideosByLocationId,
} from 'src/components/Posting/hooks';
import { useGetVideoShareUrl } from 'src/features/BestOfRing/hooks/useGetVideoShareUrl';
import { buildSubmitYourVideoEmail } from 'src/features/BestOfRing/utils/emailBuilder';
import { useHasWebViewUserAgent } from 'src/hooks';
import { AppRoutes } from 'src/routes';
import {
  Device,
  DeviceSelectorDropdown,
} from 'src/shared/components/DeviceSelectorDropdown';
import { DeviceSelectorModal } from 'src/shared/components/DeviceSelectorModal';
import {
  Location,
  LocationSelectorDropdown,
} from 'src/shared/components/LocationSelectorDropdown';
import { LocationSelectorModal } from 'src/shared/components/LocationSelectorModal';
import { MediaItemList, Video } from 'src/shared/components/MediaItemList';
import { NoMediaSelected } from 'src/shared/components/NoMediaSelected';
import { NoSubscriptionModal } from 'src/shared/components/NoSubscriptionModal';
import { SubNavbar, subNavbarHeight } from 'src/shared/components/SubNavbar';
import { toast } from 'src/shared/components/Toast';
import { VerticalDivider } from 'src/shared/components/VerticalDivider';
import { useIsiOS, useIsMobile } from 'src/shared/hooks';
import { routes } from '../routes';

const HISTORY_LIMIT = 1;

export const RingHistory = () => {
  const { t: tPosting } = useTranslation('components.posting');
  const { t } = useTranslation('features.bestOfRing');
  const [selectedLocationId, setSelectedLocationId] =
    useState<string | null>(null);
  const [selectedLocationName, setSelectedLocationName] =
    useState<string | null>(null);
  const [isChangeLocationModalOpen, setIsChangeLocationModalOpen] =
    useState(false);
  const [isChangeCamerasModalOpen, setIsChangeCamerasModalOpen] =
    useState(false);
  const [isNoSubscriptionModalOpen, setIsNoSubscriptionModalOpen] =
    useState(false);
  const [media, setMedia] = useState<Video[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device>(defaultDevice);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [
    getRingVideoShareUrl,
    { data: ringVideoShareUrlData, loading: ringVideoShareUrlLoading, called },
  ] = useGetVideoShareUrl();

  const isMobile = useIsMobile();

  const { data: ringLocations } = useGetRingLocations({
    onCompleted(data: Array<Location>) {
      setSelectedLocationId(data[0].id);
      setSelectedLocationName(data[0].name);
    },
  });

  const {
    data: locationVideos,
    loading: loadingLocationVideos,
    fetchMore: locationFetchMore,
  } = useGetVideosByLocationId(selectedLocationId);

  const deviceListQuery = useGetDevicesByLocationId(selectedLocationId, () =>
    setHasLoaded(true),
  );

  const { data: devicesList } = deviceListQuery;

  const {
    data: deviceVideos,
    loading: loadingDeviceVideos,
    fetchMore: deviceFetchMore,
  } = useGetVideosByDeviceId(selectedDevice.id);

  const loadingVideos = useMemo(
    () => loadingLocationVideos || loadingDeviceVideos,
    [loadingLocationVideos, loadingDeviceVideos],
  );

  const videos = useMemo(() => {
    let listOfVideos = deviceVideos.videos;
    if (selectedDevice.id === defaultDevice.id)
      listOfVideos = locationVideos.videos;

    return listOfVideos.filter(
      (video: Video) => video.hasSubscription && !!video.url,
    );
  }, [selectedDevice, locationVideos, deviceVideos]);

  const fetchMore = useMemo(
    () =>
      selectedDevice.id === defaultDevice.id
        ? locationFetchMore
        : deviceFetchMore,
    [selectedDevice.id, locationFetchMore, deviceFetchMore],
  );

  const hasNextPage = useMemo(
    () =>
      selectedDevice.id === defaultDevice.id
        ? locationVideos.hasNextPage
        : deviceVideos.hasNextPage,
    [selectedDevice.id, deviceVideos.hasNextPage, locationVideos.hasNextPage],
  );

  const endCursor = useMemo(
    () =>
      selectedDevice.id === defaultDevice.id
        ? locationVideos.endCursor
        : deviceVideos.endCursor,
    [selectedDevice.id, deviceVideos.endCursor, locationVideos.endCursor],
  );

  const locationName = useMemo(
    () =>
      ringLocations?.find((location) => location.id === selectedLocationId)
        ?.name,
    [selectedLocationId, ringLocations],
  );

  const defaultVideo: Video = useMemo(() => {
    const historyVideos = media.filter((m) => !(m instanceof File));
    return (historyVideos[historyVideos.length - 1] as Video) || videos[0];
  }, [media, videos]);

  const handleDeviceChange = useCallback(
    (id: string) => {
      const selectedDevice = devicesList.find(
        (device: Device) => device.id === id,
      );
      setSelectedDevice(selectedDevice);
      setIsChangeCamerasModalOpen(false);
    },
    [setSelectedDevice, devicesList],
  );

  const handleVideoCheckboxChange = useCallback(
    (video: Video) => (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setMedia([video]);
      } else {
        setMedia(media.filter((media) => media.id !== video.id));
      }
    },
    [media],
  );

  const handleVideoPreview = useCallback(
    (videoId) => () => {
      const selectedVideo = videos.find((video: Video) => video.id === videoId);
      if (selectedVideo) {
        setPlayingVideo(selectedVideo);
      }
    },
    [videos],
  );

  const handleOnLoadMoreItems = useCallback(() => {
    fetchMore({
      variables: { after: endCursor },
      updateQuery: (prevResult, options) => {
        const { fetchMoreResult } = options;
        if (!fetchMoreResult) return prevResult;
        const objectKey =
          selectedDevice.id === defaultDevice.id
            ? 'ringVideosByLocationId'
            : 'ringVideosByDeviceId';

        return {
          [objectKey]: {
            ...(fetchMoreResult as any)?.[objectKey],
            edges: [
              ...(prevResult as unknown as any)?.[objectKey]?.edges,
              ...(fetchMoreResult as unknown as any)?.[objectKey]?.edges,
            ],
          },
        };
      },
    });
  }, [endCursor, fetchMore, selectedDevice]);

  const handleLocationChange = useCallback(
    (id: string) => {
      setSelectedDevice(defaultDevice);
      setSelectedLocationId(id);
    },
    [setSelectedLocationId, setSelectedDevice],
  );
  const handlePreviewError = () => {
    toast.error(tPosting('video-preview-error'));
  };

  const toggleNoSubscriptionModal = useCallback(
    () => setIsNoSubscriptionModalOpen((s) => !s),
    [],
  );

  const checkDeviceSubscription = useCallback(
    (device: Device) =>
      checkSubscriptionWithToggler(
        device,
        devicesList,
        toggleNoSubscriptionModal,
      ),
    [devicesList, toggleNoSubscriptionModal],
  );

  useEffect(() => {
    if (!hasLoaded) return;

    if (selectedLocationId !== null) {
      checkDeviceSubscription(selectedDevice);
    }
  }, [selectedDevice, selectedLocationId, checkDeviceSubscription]);

  const [playingVideo, setPlayingVideo] = useState(defaultVideo);

  useEffect(() => {
    if (!hasLoaded) return;

    if (devicesList.every((device: any) => device?.isRecordingEnabled !== true))
      setIsNoSubscriptionModalOpen(true);
  }, [devicesList, hasLoaded]);

  const isWebView = useHasWebViewUserAgent();
  const isiOS = useIsiOS();

  const history = useHistory();

  const goBackOrBestOfRingFeed = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.replace(AppRoutes.BestOfRing + routes.BestOfRingFeed);
    }
  }, [history]);

  const onSubmitVideo = () => {
    getRingVideoShareUrl({ variables: { dingId: media[0].id } });
  };

  useEffect(() => {
    if (called && !ringVideoShareUrlLoading) {
      if (ringVideoShareUrlData?.ringVideoShareUrl?.url) {
        const shareUrl = ringVideoShareUrlData.ringVideoShareUrl?.url
          ? ringVideoShareUrlData.ringVideoShareUrl?.url
          : ringVideoShareUrlData.ringVideoShareUrl?.wrapperUrl?.replace(
              'site.',
              'www.',
            );
        const email = buildSubmitYourVideoEmail(shareUrl!);
        window.location.replace(email);
      } else {
        toast.error(
          'There was an error submitting your video. Please try again later.',
        );
      }
    }
  }, [called, ringVideoShareUrlData, ringVideoShareUrlLoading]);

  const onSubscriptionModalClose = () => {
    setIsNoSubscriptionModalOpen(false);
    history.goBack();
  };

  return (
    <Box
      position="relative"
      pt={subNavbarHeight}
      {...(() => (!isMobile ? { height: '100%', maxHeight: '100dvh' } : {}))()}
    >
      {isWebView && isiOS ? undefined : (
        <SubNavbar position="absolute" top={0} width="100%">
          <Box mx="auto" textStyle="body-normal-medium">
            Submit Your Video
          </Box>

          <CloseButton onClick={goBackOrBestOfRingFeed} />
        </SubNavbar>
      )}
      <Box
        maxWidth={{ base: '100%', m: 1150 }}
        width="100%"
        pb={6}
        {...(() =>
          !isMobile ? { height: '100%', maxHeight: '100dvh' } : {})()}
      >
        <Flex
          flexDirection={{ base: 'column-reverse', m: 'row' }}
          width="full"
          height="100%"
        >
          <Flex flexDirection="column" height="100%">
            <Flex
              flexDirection="row"
              pl={{ base: 3, s: 6, m: 10 }}
              height="100%"
            >
              <Flex
                flexDirection="column"
                width={{ base: 'full', m: '25vw' }}
                mr={{ base: 0, m: 8 }}
                mt={{ base: 0, m: 8 }}
              >
                {isMobile ? null : <Header limit={HISTORY_LIMIT} />}
                <Box
                  mb={{ base: 0, m: 0 }}
                  width={{ base: 'full', m: 'full' }}
                  minHeight={0}
                  flexGrow={1}
                  height="100%"
                >
                  <Flex flexDirection="column" height="100%">
                    {isMobile ? (
                      <>
                        <LocationSelectorModal
                          isOpen={isChangeLocationModalOpen}
                          onClose={() => setIsChangeLocationModalOpen(false)}
                          locations={ringLocations || []}
                          onChange={handleLocationChange}
                          selectedLocation={selectedLocationId!}
                        />
                        <DeviceSelectorModal
                          isOpen={isChangeCamerasModalOpen}
                          onClose={() => setIsChangeCamerasModalOpen(false)}
                          devices={devicesList}
                          onChange={handleDeviceChange}
                          selectedDevice={selectedDevice.id}
                        />
                      </>
                    ) : (
                      <>
                        <LocationSelectorDropdown
                          locations={ringLocations || []}
                          onClick={handleLocationChange}
                          selectedLocation={selectedLocationId!}
                        />
                        <DeviceSelectorDropdown
                          devices={devicesList}
                          onClick={handleDeviceChange}
                          selectedDevice={selectedDevice.id}
                        />
                      </>
                    )}
                    <Flex flexDirection="column" maxHeight="50vh" flex={1}>
                      <MediaItemList
                        items={videos}
                        isLoading={loadingVideos}
                        onChange={handleVideoCheckboxChange}
                        select={handleVideoPreview}
                        selected={media as Video[]}
                        previewing={playingVideo}
                        selectedMediaAmount={0}
                        onLoadMoreItems={handleOnLoadMoreItems}
                        hasNextPage={hasNextPage}
                        data-testid="media-item-list"
                      />
                    </Flex>
                  </Flex>
                </Box>
              </Flex>
              {isMobile ? null : <VerticalDivider />}
            </Flex>
            <Flex
              position={isMobile ? 'fixed' : 'relative'}
              bottom={0}
              left={0}
              width="100%"
              justifyContent="center"
              zIndex={100}
              background="white"
              borderColor="tertiary.500"
              borderStyle="solid"
              borderTopWidth={1}
              borderRightWidth={1}
            >
              <Button
                my={4}
                mx={6}
                size="lg"
                width="100%"
                disabled={media.length === 0 || ringVideoShareUrlLoading}
                onClick={onSubmitVideo}
              >
                {t('submit')}
              </Button>
            </Flex>
          </Flex>
          <Flex
            flexDirection="column"
            width="100%"
            ml={{ m: 10 }}
            pr={{ base: 3, s: 6, m: 10 }}
          >
            {isMobile ? <Header limit={HISTORY_LIMIT} /> : null}
            <VideoPlayerHeader
              isMobile={isMobile}
              handleChangeCamerasClick={() => setIsChangeCamerasModalOpen(true)}
              handleChangeLocationClick={() =>
                setIsChangeLocationModalOpen(true)
              }
              hasLocationSelector={ringLocations!.length >= 2}
              device={selectedDevice!.name}
              location={locationName}
              previewDeviceName={
                playingVideo?.deviceName ?? selectedDevice?.name
              }
            />
            {playingVideo && (
              <VideoPlayer
                url={playingVideo.url}
                handleError={handlePreviewError}
              />
            )}
            {!playingVideo && videos?.length > 0 && (
              <NoMediaSelected limit={HISTORY_LIMIT} />
            )}
          </Flex>
        </Flex>
        <NoSubscriptionModal
          isOpen={isNoSubscriptionModalOpen}
          onClose={onSubscriptionModalClose}
          locationId={selectedLocationId}
          locationName={selectedLocationName}
          selectedDevice={selectedDevice}
        />
      </Box>
    </Box>
  );
};
